import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Empty,
  Progress,
  Select,
  Spin,
  Splitter,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import { ArrowLeftOutlined, FileSearchOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { adminApi } from '../api'
import { BidParseSourceViewer } from '../components/BidParseSourceViewer'
import type { PreviewKind } from '../components/knowledgeFilePreview'
import { buildQuoteMatchCandidates, findQuoteRange } from '../lib/pdfQuoteHighlight'
import { useAdminAuth } from '../lib/useAdminAuth'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { scopeQuery } from '../constants/knowledgeScope'
import './DocLocate.css'

type Evidence = {
  id: number
  chunk_index: number | null
  block_id: string | null
  page_no: number | null
  quote: string
  quote_start: number | null
  quote_end: number | null
  match_type: string | null
  valid: boolean
}

type ParseField = {
  id: number
  field_code: string
  field_name: string
  value: string | null
  display_value: string | null
  original_value: string | null
  status: string
  confidence: number | null
  evidence: Evidence[]
}

type ParseTask = {
  id: number
  item_id: number
  scope: string
  template_id: number
  template_name: string | null
  status: string
  error_message: string | null
  field_total: number
  field_done: number
  fields?: ParseField[]
}

type BidTemplate = {
  id: number
  template_name: string
  description: string
  field_count?: number
}

type ChunkRow = {
  chunk_index: number
  content: string
}

const STATUS_COLOR: Record<string, string> = {
  found: 'green',
  not_found: 'default',
  ambiguous: 'orange',
  pending: 'blue',
}

function parseScope(raw: string | null): KnowledgeScope {
  return raw === 'personal' ? 'personal' : 'enterprise'
}

function validEvidenceList(field: ParseField | null | undefined): Evidence[] {
  return (field?.evidence || []).filter((e) => e.valid)
}

function EvidenceLocators({
  items,
  activeIndex,
  onSelect,
}: {
  items: Evidence[]
  activeIndex: number
  onSelect: (index: number) => void
}) {
  if (items.length <= 1) return null
  return (
    <div className="doc-locate-evidence-locs">
      <Typography.Text type="secondary" className="doc-locate-evidence-locs-label">
        依据出处（{items.length} 处）：
      </Typography.Text>
      <div className="doc-locate-evidence-locs-btns">
        {items.map((ev, i) => (
          <Button
            key={ev.id || `${ev.chunk_index}-${i}`}
            size="small"
            type={i === activeIndex ? 'primary' : 'default'}
            onClick={() => onSelect(i)}
            title={ev.quote}
          >
            {i + 1}
            {ev.page_no ? ` · p${ev.page_no}` : ''}
          </Button>
        ))}
      </div>
    </div>
  )
}

function highlightChunkText(content: string, start: number | null, end: number | null, quote: string) {
  const text = content || ''
  let s = start
  let e = end
  if (s == null || e == null || e <= s) {
    for (const cand of buildQuoteMatchCandidates(quote)) {
      const range = findQuoteRange(text, cand)
      if (range) {
        s = range[0]
        e = range[1]
        break
      }
    }
  }
  if (s == null || e == null || e <= s) {
    return { before: text, mid: '', after: '', matched: false }
  }
  return {
    before: text.slice(0, s),
    mid: text.slice(s, e),
    after: text.slice(e),
    matched: true,
  }
}

export default function DocLocateWorkspace() {
  const navigate = useNavigate()
  const { itemId: itemIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const scope = parseScope(searchParams.get('scope'))
  const scopeParams = useMemo(() => scopeQuery(scope), [scope])
  const itemId = Number(itemIdParam)
  const { ready, loggedIn } = useAdminAuth()

  const [title, setTitle] = useState('')
  const [task, setTask] = useState<ParseTask | null>(null)
  const [loading, setLoading] = useState(false)
  const [starting, setStarting] = useState(false)
  const [chunks, setChunks] = useState<ChunkRow[]>([])
  const [activeChunk, setActiveChunk] = useState<number | null>(null)
  const [activeEvidence, setActiveEvidence] = useState<Evidence | null>(null)
  const [activeEvidenceIndex, setActiveEvidenceIndex] = useState(0)
  const [activeFieldId, setActiveFieldId] = useState<number | null>(null)
  const [rightTab, setRightTab] = useState<'source' | 'evidence'>('source')
  const [previewKind, setPreviewKind] = useState<PreviewKind | null>(null)
  const [templates, setTemplates] = useState<BidTemplate[]>([])
  const [templateId, setTemplateId] = useState<number | null>(null)
  const finishNotified = useRef<number | null>(null)
  const [splitVertical, setSplitVertical] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 960px)')
    const apply = () => setSplitVertical(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  useEffect(() => {
    if (!ready || !loggedIn) return
    adminApi
      .get<{ items: BidTemplate[] }>('/admin/bid-parse/templates', {
        params: { page: 1, page_size: 100, active_only: true },
      })
      .then((r) => {
        setTemplates(r.data.items)
        setTemplateId((prev) => prev ?? r.data.items[0]?.id ?? null)
      })
      .catch(() => setTemplates([]))
  }, [ready, loggedIn])

  useEffect(() => {
    if (!ready || !loggedIn || !Number.isFinite(itemId) || itemId <= 0) return
    adminApi
      .get<{ title: string }>(`/admin/knowledge/items/${itemId}`, { params: scopeParams })
      .then((r) => setTitle(r.data.title || `文档 #${itemId}`))
      .catch(() => setTitle(`文档 #${itemId}`))
    adminApi
      .get<{ chunks: ChunkRow[] }>(`/admin/bid-parse/items/${itemId}/chunks`, { params: scopeParams })
      .then((r) => setChunks(r.data.chunks || []))
      .catch(() => setChunks([]))
  }, [ready, loggedIn, itemId, scopeParams])

  const loadLatestTask = useCallback(() => {
    if (!Number.isFinite(itemId)) return
    setLoading(true)
    adminApi
      .get<{ items: ParseTask[] }>('/admin/bid-parse/tasks', {
        params: { ...scopeParams, item_id: itemId, page: 1, page_size: 1 },
      })
      .then(async (r) => {
        const t = r.data.items[0]
        if (!t) {
          setTask(null)
          return
        }
        const detail = await adminApi.get<ParseTask>(`/admin/bid-parse/tasks/${t.id}`)
        setTask(detail.data)
        if (detail.data.template_id) setTemplateId(detail.data.template_id)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [itemId, scopeParams])

  useEffect(() => {
    if (ready && loggedIn) loadLatestTask()
  }, [ready, loggedIn, loadLatestTask])

  useEffect(() => {
    if (task?.status !== 'running' || !task.id) return
    const timer = window.setInterval(() => {
      adminApi
        .get<ParseTask>(`/admin/bid-parse/tasks/${task.id}`)
        .then((r) => setTask(r.data))
        .catch(() => {})
    }, 2000)
    return () => window.clearInterval(timer)
  }, [task?.status, task?.id])

  useEffect(() => {
    if (!task?.id) return
    if (task.status === 'running') {
      finishNotified.current = null
      return
    }
    if (finishNotified.current === task.id) return
    if (task.status === 'done') {
      finishNotified.current = task.id
      message.success('解析完成')
      loadLatestTask()
    } else if (task.status === 'failed') {
      finishNotified.current = task.id
      message.error(task.error_message || '解析失败')
    }
  }, [task?.id, task?.status, task?.error_message, loadLatestTask])

  const startParse = () => {
    if (!templateId) {
      message.warning('请选择解析模板')
      return
    }
    setStarting(true)
    finishNotified.current = null
    adminApi
      .post<ParseTask>('/admin/bid-parse/tasks', {
        item_id: itemId,
        scope,
        template_id: templateId,
      })
      .then((r) => adminApi.post<ParseTask>(`/admin/bid-parse/tasks/${r.data.id}/run`))
      .then((r) => {
        setTask(r.data)
        message.info('解析已开始，请稍候…')
      })
      .catch((e) => message.error(e.message))
      .finally(() => setStarting(false))
  }

  const onLocate = (field: ParseField) => {
    setActiveFieldId(field.id)
    const list = validEvidenceList(field)
    if (!list.length) {
      message.info(field.status === 'not_found' ? '未找到相关要求，无法定位' : '无有效证据')
      setActiveEvidence(null)
      setActiveEvidenceIndex(0)
      return
    }
    setActiveEvidenceIndex(0)
    setActiveEvidence(list[0])
    if (list[0].chunk_index != null) setActiveChunk(list[0].chunk_index)
    setRightTab('source')
  }

  const selectEvidenceAt = (field: ParseField | null, index: number) => {
    const list = validEvidenceList(field)
    const ev = list[index]
    if (!ev) return
    setActiveEvidenceIndex(index)
    setActiveEvidence(ev)
    if (ev.chunk_index != null) setActiveChunk(ev.chunk_index)
  }

  const activeField = useMemo(
    () => task?.fields?.find((f) => f.id === activeFieldId) ?? null,
    [task?.fields, activeFieldId],
  )

  const locatableEvidence = useMemo(() => validEvidenceList(activeField), [activeField])

  const chunkContent = useMemo(() => {
    if (activeChunk == null) return ''
    return chunks.find((c) => c.chunk_index === activeChunk)?.content || ''
  }, [activeChunk, chunks])

  const hl = useMemo(() => {
    if (!activeEvidence || !chunkContent) return null
    return highlightChunkText(
      chunkContent,
      activeEvidence.quote_start,
      activeEvidence.quote_end,
      activeEvidence.quote,
    )
  }, [activeEvidence, chunkContent])

  const sourceTabLabel = useMemo(() => {
    if (previewKind === 'pdf') {
      return activeEvidence?.page_no != null && activeEvidence.page_no > 0
        ? `PDF 原文（第 ${activeEvidence.page_no} 页）`
        : 'PDF 原文'
    }
    if (previewKind === 'docx' || previewKind === 'doc') return 'Word 原文'
    return '原文预览'
  }, [previewKind, activeEvidence?.page_no])

  const parsing = task?.status === 'running'
  const progressPercent =
    task && task.field_total > 0
      ? Math.min(100, Math.round((task.field_done / task.field_total) * 100))
      : parsing
        ? 0
        : 100

  if (!ready) {
    return (
      <div className="doc-locate-workspace">
        <Spin />
      </div>
    )
  }

  if (!loggedIn) {
    return (
      <div className="doc-locate-workspace">
        <Alert type="info" message="请先登录管理账号" />
      </div>
    )
  }

  return (
    <div className="doc-locate-workspace workspace-root">
      <div className="doc-locate-toolbar">
        <div className="doc-locate-toolbar-title">
          <Typography.Title level={4} style={{ margin: 0 }}>
            文档定位
          </Typography.Title>
          <Typography.Text type="secondary" ellipsis style={{ display: 'block', maxWidth: 480 }}>
            {title || `文档 #${itemId}`}
          </Typography.Text>
        </div>
        <div className="doc-locate-toolbar-actions">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/locate')}>
            更换文档
          </Button>
          <Select
            style={{ minWidth: 200 }}
            placeholder="解析模板"
            value={templateId ?? undefined}
            onChange={setTemplateId}
            disabled={parsing}
            options={templates.map((t) => ({
              value: t.id,
              label: `${t.template_name}（${t.field_count ?? 0} 项）`,
            }))}
          />
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={starting || parsing}
            disabled={parsing || !templateId}
            onClick={startParse}
          >
            {task ? '重新解析' : '开始解析'}
          </Button>
        </div>
      </div>

      {parsing ? (
        <Progress
          percent={progressPercent}
          status="active"
          size="small"
          format={() => (task?.field_total ? `${task.field_done}/${task.field_total}` : '准备中…')}
        />
      ) : null}

      <div className="doc-locate-card workspace-card">
        <Splitter
          className="doc-locate-splitter"
          layout={splitVertical ? 'vertical' : 'horizontal'}
          lazy
        >
          <Splitter.Panel
            className="doc-locate-aside-panel"
            defaultSize={splitVertical ? '42%' : 380}
            min={splitVertical ? '28%' : 260}
            max={splitVertical ? '65%' : '55%'}
          >
            <aside className="doc-locate-aside">
              <div className="doc-locate-aside-head">
                解析结果
                {task?.template_name ? (
                  <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                    · {task.template_name}
                  </Typography.Text>
                ) : null}
              </div>
              <div className="doc-locate-aside-body">
                {loading ? (
                  <Spin />
                ) : !task?.fields?.length && !parsing ? (
                  <Empty description="暂无结果，请选择模板并点击「开始解析」" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : task?.fields?.length ? (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={task.fields}
                    onRow={(row) => ({
                      onClick: () => onLocate(row),
                      className: [
                        row.evidence?.some((e) => e.valid) ? 'doc-locate-row-locatable' : '',
                        row.id === activeFieldId ? 'doc-locate-row-active' : '',
                      ]
                        .filter(Boolean)
                        .join(' '),
                    })}
                    columns={[
                      { title: '字段', dataIndex: 'field_name', ellipsis: true, width: 120 },
                      {
                        title: '结果',
                        render: (_: unknown, r: ParseField) => r.display_value || r.value || '—',
                        ellipsis: true,
                      },
                      {
                        title: '状态',
                        dataIndex: 'status',
                        width: 72,
                        render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
                      },
                      {
                        title: '',
                        width: 56,
                        render: (_: unknown, r: ParseField) => {
                          const n = validEvidenceList(r).length
                          if (!n) return null
                          return (
                            <span className="doc-locate-locate-badge" title={`${n} 处依据`}>
                              <FileSearchOutlined />
                              {n > 1 ? <sup>{n}</sup> : null}
                            </span>
                          )
                        },
                      },
                    ]}
                  />
                ) : (
                  <Typography.Text type="secondary">正在解析…</Typography.Text>
                )}
                {task?.status === 'failed' && task.error_message ? (
                  <Alert type="error" message={task.error_message} style={{ marginTop: 12 }} />
                ) : null}
              </div>
            </aside>
          </Splitter.Panel>

          <Splitter.Panel className="doc-locate-main-panel" min={splitVertical ? '35%' : 320}>
            <main className="doc-locate-main">
              <div className="doc-locate-main-head">
                <Tabs
                  activeKey={rightTab}
                  onChange={(k) => setRightTab(k as 'source' | 'evidence')}
                  items={[
                    { key: 'source', label: sourceTabLabel },
                    { key: 'evidence', label: '切片证据' },
                  ]}
                />
              </div>
              <div className="doc-locate-main-body">
                {rightTab === 'source' ? (
                  <div className="doc-locate-preview-wrap">
                    <EvidenceLocators
                      items={locatableEvidence}
                      activeIndex={activeEvidenceIndex}
                      onSelect={(i) => selectEvidenceAt(activeField, i)}
                    />
                    {activeEvidence?.quote ? (
                      <blockquote className="doc-locate-quote">{activeEvidence.quote}</blockquote>
                    ) : (
                      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        点击左侧有证据的字段，在 PDF / Word 原文中标红定位
                      </Typography.Text>
                    )}
                    <BidParseSourceViewer
                      itemId={itemId}
                      scopeParams={scopeParams}
                      active={ready && loggedIn && Number.isFinite(itemId)}
                      pageNo={activeEvidence?.page_no ?? null}
                      quote={activeEvidence?.quote ?? null}
                      onPreviewKind={setPreviewKind}
                      minHeight="calc(100vh - 240px)"
                    />
                  </div>
                ) : !activeEvidence ? (
                  <Typography.Text type="secondary">点击左侧字段查看切片证据</Typography.Text>
                ) : !chunkContent ? (
                  <Empty description="未加载到对应切片" />
                ) : (
                  <>
                    <EvidenceLocators
                      items={locatableEvidence}
                      activeIndex={activeEvidenceIndex}
                      onSelect={(i) => selectEvidenceAt(activeField, i)}
                    />
                    <blockquote className="doc-locate-quote">{activeEvidence.quote}</blockquote>
                    <pre className="doc-locate-chunk-text">
                      {hl?.before ?? chunkContent}
                      {hl?.matched ? <mark className="doc-locate-hl">{hl.mid}</mark> : null}
                      {hl?.after ?? ''}
                    </pre>
                    {activeEvidence.block_id ? (
                      <Typography.Text type="secondary" className="doc-locate-meta">
                        block: {activeEvidence.block_id} · {activeEvidence.match_type}
                        {activeEvidence.page_no ? ` · 约第 ${activeEvidence.page_no} 页` : ''}
                      </Typography.Text>
                    ) : null}
                  </>
                )}
              </div>
            </main>
          </Splitter.Panel>
        </Splitter>
      </div>
    </div>
  )
}
